import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./profile/types";

export const usePostsQuery = (profile: Profile | undefined) => {
  return useQuery({
    queryKey: ["posts", profile?.gym_id, profile?.role],
    queryFn: async () => {
      console.log("=== POSTS QUERY START ===");
      console.log("Fetching posts for user:", {
        role: profile?.role,
        gym_id: profile?.gym_id,
        user_id: profile?.id
      });
      
      // Validación básica: usuario debe estar autenticado y tener perfil
      if (!profile) {
        console.warn("No profile available for posts query");
        return [];
      }

      // Obtener gimnasios administrados por el manager (para filtrado defensivo)
      let managedGyms: string[] = [];
      if (profile.role === 'manager') {
        try {
          const { data: gymManagersData } = await supabase
            .from("gym_managers")
            .select("gym_id")
            .eq("manager_id", profile.id);
          
          managedGyms = gymManagersData?.map(gm => gm.gym_id).filter(Boolean) || [];
          console.log("Manager administra estos gimnasios:", managedGyms);
        } catch (error) {
          console.error("Error obteniendo gimnasios administrados:", error);
        }
      }

      // Determinar gimnasios válidos para este usuario
      const validGymIds: string[] = [];
      if (profile.gym_id) validGymIds.push(profile.gym_id);
      validGymIds.push(...managedGyms);
      
      console.log("Gimnasios válidos para este usuario:", validGymIds);

      // Validación específica por rol
      if (profile.role === 'socio' && !profile.gym_id) {
        console.warn("Cliente sin gimnasio asignado, no puede ver posts");
        return [];
      }

      if (profile.role === 'profesor' && !profile.gym_id) {
        console.warn("Profesor sin gimnasio asignado, no puede ver posts");
        return [];
      }

      if (profile.role === 'manager' && validGymIds.length === 0) {
        console.warn("Manager sin gimnasios asignados o administrados, no puede ver posts");
        return [];
      }

      // Query básica - las políticas RLS se encargan del filtrado
      const query = supabase
        .from("posts")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(3);

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching posts:", error);
        throw error;
      }

      console.log("Posts devueltos por Supabase (antes del filtrado):");
      data?.forEach((post, index) => {
        console.log(`Post ${index + 1}:`, {
          id: post.id,
          title: post.title,
          gym_id: post.gym_id,
          created_at: post.created_at
        });
      });

      // Filtro defensivo adicional en frontend
      let filteredPosts = data || [];

      // 1. Eliminar posts sin gym_id
      filteredPosts = filteredPosts.filter(post => post.gym_id !== null);

      // 2. Para managers, filtrar solo posts de gimnasios válidos
      if (profile.role === 'manager' && validGymIds.length > 0) {
        const beforeManagerFilter = filteredPosts.length;
        filteredPosts = filteredPosts.filter(post => 
          post.gym_id && validGymIds.includes(post.gym_id)
        );
        console.log(`Filtro manager: ${beforeManagerFilter} -> ${filteredPosts.length} posts`);
      }

      // 3. Para socios y profesores, filtrar solo posts de su gimnasio
      if ((profile.role === 'socio' || profile.role === 'profesor') && profile.gym_id) {
        const beforeRoleFilter = filteredPosts.length;
        filteredPosts = filteredPosts.filter(post => post.gym_id === profile.gym_id);
        console.log(`Filtro ${profile.role}: ${beforeRoleFilter} -> ${filteredPosts.length} posts`);
      }

      console.log("Posts finales después del filtrado defensivo:");
      filteredPosts.forEach((post, index) => {
        console.log(`Post final ${index + 1}:`, {
          id: post.id,
          title: post.title,
          gym_id: post.gym_id
        });
      });

      console.log(`=== POSTS QUERY END: ${filteredPosts.length} posts válidos ===`);
      return filteredPosts;
    },
    enabled: !!profile, // Solo ejecuta la query cuando tengamos el perfil del usuario
    staleTime: 0, // Forzar refetch inmediato para testing
    refetchOnMount: true, // Refetch cuando el componente se monte
  });
};